import { AlertCircle, CheckCircle2, CheckSquare, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import type { OverdueItem } from "../types";
import { Card, CardHeader, Empty, SkeletonRow } from "./_shared";

type Props = {
  items: OverdueItem[];
  isLoading?: boolean;
};

function daysOverdue(iso: string | null): number {
  if (!iso) return 0;
  const due = new Date(iso);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(
    0,
    Math.round((today.getTime() - due.getTime()) / (24 * 3600 * 1000)),
  );
}

export function OverdueItemsWidget({ items, isLoading }: Props) {
  return (
    <Card
      style={{
        borderLeft: items.length > 0 ? "4px solid hsl(0,70%,55%)" : undefined,
      }}
    >
      <CardHeader
        icon={
          <AlertCircle
            className="h-4 w-4 text-[hsl(0,70%,45%)]"
            strokeWidth={1.5}
          />
        }
        title="Overdue Items"
        count={items.length}
        accent={items.length > 0 ? "hsl(0,70%,45%)" : undefined}
      />
      {isLoading ? (
        <div>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Empty
          icon={
            <CheckCircle2
              className="h-8 w-8 text-[hsl(142,72%,28%)]"
              strokeWidth={1.5}
            />
          }
          text="No overdue items — great work! ✅"
          variant="success"
        />
      ) : (
        <ul className="divide-y" style={{ borderColor: "hsl(210,30%,92%)" }}>
          {items.slice(0, 5).map((it) => {
            const Icon = it.kind === "task" ? CheckSquare : Zap;
            const days = daysOverdue(it.dueDate);
            return (
              <li key={it.id}>
                <Link
                  to={`/${it.kind === "task" ? "tasks" : "actions"}/${it.id}`}
                  className="flex items-center gap-2 py-2 hover:bg-[hsl(210,50%,98%)]"
                >
                  <Icon
                    className="h-3.5 w-3.5 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <span className="truncate text-[13px] font-medium text-foreground">
                    {it.title}
                  </span>
                  <span
                    className="ml-auto shrink-0 text-[11px] font-semibold"
                    style={{ color: "hsl(0,70%,40%)" }}
                  >
                    {days} day{days === 1 ? "" : "s"} overdue
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
